import React from "react";
import { Pencil, Trash2, Link2, History, ShoppingCart, FileText } from "lucide-react";
import { fmt, fmtDate, DEBT_CATEGORIES, catColor, catLabel } from "@/lib/constants";
import { utilTone, utilBar } from "@/components/debts/CreditUtilization";

export default function DebtTable({ title, subtitle, debts, cards, emptyText, testid, onEdit, onDelete, onHistory, onCharge }) {
  return (
    <div className="rounded-2xl border border-border bg-card overflow-hidden" data-testid={testid}>
      <div className="p-4 border-b border-border flex items-center justify-between gap-3">
        <div>
          <div className="font-display font-bold text-lg">{title}</div>
          <div className="text-xs text-muted-foreground">{subtitle}</div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Total</div>
          <div className="font-mono font-bold" data-testid={`${testid}-total`}>{fmt(debts.reduce((s, d) => s + d.balance, 0))}</div>
        </div>
      </div>
      <div className="overflow-x-auto thin-scroll">
        <table className="w-full text-sm">
          <thead className="bg-accent/40 border-b border-border">
            <tr className="text-left text-[10px] uppercase tracking-widest font-bold text-muted-foreground">
              <th className="p-3">Name</th>
              <th className="p-3">Progress</th>
              <th className="p-3 text-right">Balance</th>
              {cards && <th className="p-3">Utilization</th>}
              <th className="p-3 text-right">APR</th>
              <th className="p-3 text-right">Min pmt</th>
              <th className="p-3">Next due</th>
              <th className="p-3 text-right">Interest paid</th>
              <th className="p-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {debts.length === 0 && <tr><td colSpan={cards ? 9 : 8} className="p-8 text-center text-muted-foreground">{emptyText}</td></tr>}
            {debts.map(d => (
              <tr key={d.id} data-testid={`debt-row-${d.id}`} className={`border-b border-border/60 hover:bg-accent/30 ${d.paid_off ? "opacity-60" : ""}`}>
                <td className="p-3">
                  <div className="flex items-center gap-2">
                    <span className="h-2 w-2 rounded-full" style={{ background: catColor(DEBT_CATEGORIES, d.category) }} />
                    <div>
                      <div className={`font-semibold flex items-center gap-2 ${d.paid_off ? "line-through" : ""}`}>
                        {d.name}
                        {d.idle && <span className="px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider bg-emerald-500/15 text-emerald-500 border border-emerald-500/30 no-underline" data-testid={`debt-idle-${d.id}`}>No balance</span>}
                      </div>
                      <div className="text-[10px] text-muted-foreground">{catLabel(DEBT_CATEGORIES, d.category)}</div>
                    </div>
                  </div>
                </td>
                <td className="p-3 min-w-[160px]">
                  {d.idle ? <span className="text-xs text-muted-foreground">Open · nothing owed</span> : (
                    <>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-2 rounded-full bg-accent overflow-hidden">
                          <div className={`h-full rounded-full ${d.paid_off ? "bg-emerald-500" : "bg-teal-500"}`} style={{ width: `${Math.max(d.pct_paid, 1)}%` }} />
                        </div>
                        <span className="font-mono text-xs w-12 text-right" data-testid={`debt-pct-${d.id}`}>{d.pct_paid}%</span>
                      </div>
                      <div className="text-[10px] text-muted-foreground mt-0.5">started at {fmt(d.starting_balance)}</div>
                    </>
                  )}
                </td>
                <td className="p-3 text-right font-mono font-semibold" data-testid={`debt-balance-${d.id}`}>{fmt(d.balance)}</td>
                {cards && (
                  <td className="p-3 min-w-[120px]">
                    {d.utilization != null ? (
                      <div data-testid={`debt-util-${d.id}`}>
                        <div className="flex items-center gap-2">
                          <div className={`flex-1 h-1.5 rounded-full overflow-hidden ${d.utilization <= 0 ? "bg-emerald-500" : "bg-accent"}`}>
                            <div className={`h-full rounded-full ${utilBar(d.utilization)}`} style={{ width: `${Math.min(d.utilization, 100)}%` }} />
                          </div>
                          <span className={`font-mono text-xs w-12 text-right ${utilTone(d.utilization)}`}>{d.utilization}%</span>
                        </div>
                        <div className="text-[10px] text-muted-foreground mt-0.5">of {fmt(d.credit_limit)} limit</div>
                      </div>
                    ) : <span className="text-xs text-muted-foreground" title="Add a credit limit to track utilization">No limit set</span>}
                  </td>
                )}
                <td className="p-3 text-right font-mono">
                  {d.promo_active ? (
                    <div data-testid={`debt-promo-${d.id}`}>
                      <div className="text-violet-400 font-semibold">{d.effective_rate}%</div>
                      <div className="text-[10px] text-muted-foreground font-sans">promo · {d.interest_rate}% after {fmtDate(d.promo_end)}{d.promo_days_left <= 30 && <span className="text-amber-500"> ({d.promo_days_left}d)</span>}</div>
                    </div>
                  ) : `${d.interest_rate}%`}
                </td>
                <td className="p-3 text-right font-mono">{fmt(d.minimum_payment)}</td>
                <td className="p-3 font-mono text-xs" data-testid={`debt-due-${d.id}`}>
                  {d.paid_off ? <span className="text-emerald-500">Paid off</span>
                    : d.idle ? <span className="text-muted-foreground">—</span>
                    : <span className="inline-flex items-center gap-1"><Link2 className="h-3 w-3 text-indigo-400" />{fmtDate(d.due_date)}</span>}
                  {cards && d.next_statement_date && (
                    <div className={`mt-0.5 flex items-center gap-1 text-[10px] font-sans ${d.days_to_statement <= 3 ? "text-amber-500" : "text-muted-foreground"}`} data-testid={`debt-statement-${d.id}`}>
                      <FileText className="h-3 w-3" /> Reports {fmtDate(d.next_statement_date)}{d.days_to_statement != null && ` · ${d.days_to_statement === 0 ? "today" : `${d.days_to_statement}d`}`}
                    </div>
                  )}
                </td>
                <td className="p-3 text-right font-mono text-xs text-rose-500">{fmt(d.interest_paid)}</td>
                <td className="p-3">
                  <div className="flex items-center justify-end gap-1">
                    {cards && onCharge && (
                      <button onClick={() => onCharge(d)} title="Log a charge / purchase" className="p-1.5 rounded-md hover:bg-rose-500/10 text-rose-400" data-testid={`debt-charge-${d.id}`}><ShoppingCart className="h-4 w-4" /></button>
                    )}
                    <button onClick={() => onHistory(d)} title="Payment history" className="p-1.5 rounded-md hover:bg-indigo-500/10 text-indigo-400" data-testid={`debt-history-${d.id}`}><History className="h-4 w-4" /></button>
                    <button onClick={() => onEdit(d)} className="p-1.5 rounded-md hover:bg-accent" data-testid={`debt-edit-${d.id}`}><Pencil className="h-4 w-4" /></button>
                    <button onClick={() => onDelete(d)} className="p-1.5 rounded-md hover:bg-rose-500/10 text-rose-500" data-testid={`debt-delete-${d.id}`}><Trash2 className="h-4 w-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
